import React from "react";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { cn } from "@/lib/utils";
import {
  buildNotification,
  GenerateNotification,
  isFromValid,
  isValidate,
  Loader,
} from "smart-react";
import {
  TOGGLE_CANCEL_DATATABLE_SLIDER,
  TOGGLE_CLOSE_DATATABLE_SLIDER,
} from "../../../../../constants/actionTypes";
import { LOOKUP_TYPES } from "../../../../../constants/applicationConstants";
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from "../../../../../constants/eventDataTypes";
import {
  ERROR_MESSAGE,
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
} from "../../../../../constants/notificationMessages";
import { findTenant } from "../../../../../Utils/Auth/ValidatePermissions";
import { retrieveAndDecrypt } from "../../../../../Utils/Storage/localStorageUtil";
import {
  createRFProfileSetup,
  updateRFProfileSetup,
  getRFProfileSetup,
} from "../../Services/RFProfileSetupService";
import DataFieldContentSlider from "../../../../Core/Components/DataFieldContentSlider/DataFieldContentSlider";
import DataField from "../../../../Core/Components/DataFieldContentSlider/DataField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";

type ActionMeta = {
  profile: any;
  checkedDataItem?: any;
  edit?: boolean;
};

type Props = {
  title: string;
  handleSlide: (arg?: any) => void;
  actionMeta: ActionMeta;
  updateData: () => void;
  setSelectedState: (v: any) => void;
  dataTableName: string;
  onSaveRef?: React.MutableRefObject<(() => void) | null>;
  hideInlineFooter?: boolean;
};

type FormData = {
  tenant_id?: any;
  tenant_name?: string;
  SRFProfilesId?: any;
  SRFProfilesName?: string;
  SRFProfilesSetupId?: any;
  SRFProfilesSetupName?: string;
  SRFProfilesDetails?: string;
  WHID?: string;
  RFVenName?: string;
  MTFURL?: string;
  MTFProtocol?: string;
  MTFPort?: string | number;
};

const requiredFields: (keyof FormData)[] = [
  "SRFProfilesSetupName",
  "WHID",
  "RFVenName",
  "MTFURL",
  "MTFProtocol",
  "MTFPort",
];

const AddRFProfileSetupTSX: React.FC<Props> = ({
  title,
  handleSlide,
  actionMeta,
  updateData,
  onSaveRef,
  hideInlineFooter,
}) => {
  const { profile, checkedDataItem } = actionMeta;

  const tempTenant = findTenant(profile) as any;
  const [loading, setLoading] = React.useState(false);
  const [validateForm, setValidateForm] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const [showDFModal, setShowDFModal] = React.useState(false);
  const [editorField, setEditorField] = React.useState<any>({
    name: "",
    value: "",
    label: "",
    title: "",
  });

  const [formData, setFormData] = React.useState<FormData>({
    tenant_id: profile?.TenantId,
    tenant_name: (tempTenant as any)?.TenantName,
    SRFProfilesId: profile?.SRFProfileId,
    SRFProfilesName: profile?.SRFProfilesName,
    SRFProfilesSetupId: "",
    SRFProfilesSetupName: "",
    SRFProfilesDetails: "",
    WHID: "",
    RFVenName: "",
    MTFURL: "",
    MTFProtocol: "",
    MTFPort: "",
  });

  React.useEffect(() => {
    const maybeFetchDetail = async () => {
      if (!checkedDataItem) return;

      // If we only have an id (deep-link) fetch full details first
      if ((checkedDataItem as any)?.SRFProfilesSetupId) {
        try {
          setLoading(true);
          const resp: any = await getRFProfileSetup({
            data: {
              SRFProfilesSetupId: (checkedDataItem as any)?.SRFProfilesSetupId,
            },
            moduleName: "RFProfileSetup",
            isServiceWorker: false,
            listController: undefined,
          });
          const payload = resp?.Payload || checkedDataItem;
          setFormData({ ...(payload as any) });
        } finally {
          setLoading(false);
        }
      } else {
        setFormData({ ...(checkedDataItem as any) });
      }
    };
    maybeFetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedDataItem]);

  const mtfProtocol: { Value: string; DisplayName: string }[] =
    (retrieveAndDecrypt(LOOKUP_TYPES?.mtfProtocol) as any[]) || [];

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | { target: { name: string; value?: any; type?: string; checked?: any } },
  ) => {
    const { name, value, type } = (e as any).target;
    const newValue =
      type === "checkbox" ? ((e as any).target.checked ? 1 : 0) : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const onSave = async () => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }

    setLoading(true);
    let notificationMetaData: any = {};
    let response: any;
    let method: typeof createRFProfileSetup | typeof updateRFProfileSetup;

    if (actionMeta?.edit) {
      method = updateRFProfileSetup;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createRFProfileSetup;
      notificationMetaData = CREATE_MESSAGE;
    }

    try {
      setValidateForm(false);
      response = await method({
        data: { ...formData },
        moduleName: "RFProfileSetup",
        isServiceWorker: false,
        listController: undefined,
      });

      if (!response?.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response?.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );

        updateData();

        if (actionMeta?.edit) {
          handleSlide({ action: TOGGLE_CANCEL_DATATABLE_SLIDER });
        } else {
          // if creating and staying open, reset form; else close (to match previous behavior you can tweak)
          setFormData({
            tenant_id: profile?.TenantId,
            tenant_name: (tempTenant as any)?.TenantName,
            SRFProfilesId: profile?.SRFProfileId,
            SRFProfilesName: profile?.SRFProfilesName,
            SRFProfilesSetupId: "",
            SRFProfilesSetupName: "",
            SRFProfilesDetails: "",
            WHID: "",
            RFVenName: "",
            MTFURL: "",
            MTFProtocol: "",
            MTFPort: "",
          });
          handleSlide({ action: TOGGLE_CANCEL_DATATABLE_SLIDER });
        }
      }
    } catch {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (onSaveRef) {
      onSaveRef.current = onSave;
    }
  }, [onSaveRef, onSave]);

  return (
    <div className="space-y-4">
      {loading && (
        <div className="py-4">
          <Loader />
        </div>
      )}

      {/* Profile Name (read-only) */}
      <div className="space-y-1">
        <Label
          htmlFor="SRFProfilesName"
          className="text-xs text-muted-foreground"
        >
          Profile Name
        </Label>
        <Input
          id="SRFProfilesName"
          name="SRFProfilesName"
          value={formData?.SRFProfilesName || ""}
          readOnly
          disabled
          className="h-10 bg-background"
        />
      </div>

      {/* Setup Name */}
      <div
        className={cn(
          "space-y-1",
          isValidate(validateForm, formData?.SRFProfilesSetupName) &&
            "text-destructive",
        )}
      >
        <Label
          htmlFor="SRFProfilesSetupName"
          className="text-xs text-muted-foreground"
        >
          Setup Name*
        </Label>
        <Input
          id="SRFProfilesSetupName"
          name="SRFProfilesSetupName"
          value={formData?.SRFProfilesSetupName || ""}
          onChange={handleInputChange}
          className="h-10 bg-background"
        />
        {isValidate(validateForm, formData?.SRFProfilesSetupName) && (
          <p className="text-xs mt-1">This field cannot be empty</p>
        )}
      </div>

      {/* Description + expand */}
      <div className="space-y-1 relative">
        <Label
          htmlFor="SRFProfilesDetails"
          className="text-xs text-muted-foreground"
        >
          Description
        </Label>
        <textarea
          id="SRFProfilesDetails"
          name="SRFProfilesDetails"
          value={formData?.SRFProfilesDetails || ""}
          onChange={handleInputChange}
          rows={3}
          className="h-24 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground shadow-sm focus:outline-none leading-[1.4]"
        />
      </div>

      {/* WHID */}
      <div
        className={cn(
          "space-y-1",
          isValidate(validateForm, formData?.WHID) && "text-destructive",
        )}
      >
        <Label htmlFor="WHID" className="text-xs text-muted-foreground">
          Warehouse Id*
        </Label>
        <Input
          id="WHID"
          name="WHID"
          value={formData?.WHID || ""}
          onChange={handleInputChange}
          className="h-10 bg-background"
        />
        {isValidate(validateForm, formData?.WHID) && (
          <p className="text-xs mt-1">This field cannot be empty</p>
        )}
      </div>

      {/* RF Ven Name */}
      <div
        className={cn(
          "space-y-1",
          isValidate(validateForm, formData?.RFVenName) && "text-destructive",
        )}
      >
        <Label htmlFor="RFVenName" className="text-xs text-muted-foreground">
          RF Ven Name*
        </Label>
        <Input
          id="RFVenName"
          name="RFVenName"
          value={formData?.RFVenName || ""}
          onChange={handleInputChange}
          className="h-10 bg-background"
        />
        {isValidate(validateForm, formData?.RFVenName) && (
          <p className="text-xs mt-1">This field cannot be empty</p>
        )}
      </div>

      {/* MTF URL */}
      <div
        className={cn(
          "space-y-1",
          isValidate(validateForm, formData?.MTFURL) && "text-destructive",
        )}
      >
        <Label htmlFor="MTFURL" className="text-xs text-muted-foreground">
          MTF URL*
        </Label>
        <Input
          id="MTFURL"
          name="MTFURL"
          value={formData?.MTFURL || ""}
          onChange={handleInputChange}
          className="h-10 bg-background"
        />
        {isValidate(validateForm, formData?.MTFURL) && (
          <p className="text-xs mt-1">This field cannot be empty</p>
        )}
      </div>

      {/* MTF Port */}
      <div
        className={cn(
          "space-y-1",
          isValidate(validateForm, formData?.MTFPort as any) &&
            "text-destructive",
        )}
      >
        <Label htmlFor="MTFPort" className="text-xs text-muted-foreground">
          MTF Port*
        </Label>
        <Input
          id="MTFPort"
          name="MTFPort"
          type="number"
          value={String(formData?.MTFPort ?? "")}
          onChange={handleInputChange}
          className="h-10 bg-background"
        />
        {isValidate(validateForm, formData?.MTFPort as any) && (
          <p className="text-xs mt-1">This field cannot be empty</p>
        )}
      </div>

      {/* MTF Protocol */}
      <div
        className={cn(
          "space-y-1",
          isValidate(validateForm, formData?.MTFProtocol) && "text-destructive",
        )}
      >
        <Label htmlFor="MTFProtocol" className="text-xs text-muted-foreground">
          MTF Protocol*
        </Label>

        <Select
          value={formData?.MTFProtocol || ""}
          onValueChange={(v) =>
            handleInputChange({ target: { name: "MTFProtocol", value: v } })
          }
        >
          <SelectTrigger aria-label="MTF Protocol">
            <SelectValue placeholder="Select MTF Protocol ..." />
          </SelectTrigger>
          <SelectContent>
            {Array.isArray(mtfProtocol) && mtfProtocol.length > 0 ? (
              mtfProtocol.map((opt: any) => (
                <SelectItem key={String(opt?.Value)} value={String(opt?.Value)}>
                  {opt?.DisplayName ?? String(opt?.Value ?? "")}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="" disabled>
                No options
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        {isValidate(validateForm, formData?.MTFProtocol) && (
          <p className="text-xs mt-1">This field cannot be empty</p>
        )}
      </div>

      {/* Footer actions (conditionally hidden when parent renders footer) */}
      {!hideInlineFooter && (
        <div className="sticky bottom-0 z-10 border-t border-border bg-popover px-4 sm:px-6 py-3 mt-4">
          <div className="flex justify-end gap-2">
            {!isSubmitted ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    handleSlide({
                      action: TOGGLE_CANCEL_DATATABLE_SLIDER,
                    })
                  }
                >
                  Cancel
                </Button>
                <Button type="button" onClick={onSave}>
                  Save
                </Button>
              </>
            ) : (
              <Button
                type="button"
                onClick={() =>
                  handleSlide({ action: TOGGLE_CLOSE_DATATABLE_SLIDER })
                }
              >
                Close
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Data field modal (keep legacy components) */}
      <div className="data-field-section">
        <DataFieldContentSlider show={showDFModal as any}>
          <DataField
            handleSlide={() => setShowDFModal(false)}
            editorField={editorField}
            setFormData={setFormData as any}
            formData={formData as any}
          />
        </DataFieldContentSlider>
      </div>
    </div>
  );
};

export default AddRFProfileSetupTSX;
